import React, { useState, useEffect, useMemo } from "react";
import { useTableColumnFilters, FilterTh } from "./tableFilters";
import { WorkshopCostItem, CostItem, CostCategory } from "../types";
import { costService } from "../services/dataService";
import { formatCurrency, turkishIncludes } from "../utils/formatters";
import AlertModal from "./AlertModal";
import ConfirmModal from "./ConfirmModal";
import "../pages/PurchaseOrders.css";
import "./WorkshopCostsModal.css";

const WI = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  coin: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.3c0-1 1.1-1.8 2.5-1.8s2.5.7 2.5 1.8-1 1.5-2.5 1.9-2.5.8-2.5 1.9 1.1 1.6 2.5 1.6 2.5-.7 2.5-1.6" /></svg>,
};

interface Props {
  workshopId: string;
  workshopName: string;
  isOpen: boolean;
  onClose: () => void;
}

const WorkshopCostsModal: React.FC<Props> = ({
  workshopId,
  workshopName,
  isOpen,
  onClose,
}) => {
  const [workshopCosts, setWorkshopCosts] = useState<WorkshopCostItem[]>([]);
  const [availableCostItems, setAvailableCostItems] = useState<CostItem[]>([]);
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WorkshopCostItem | null>(null);
  const [formData, setFormData] = useState({ costItemId: "", notes: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    if (isOpen && workshopId) {
      loadWorkshopCosts();
      loadAvailableCostItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, workshopId]);

  const loadWorkshopCosts = async () => {
    if (!workshopId) return;
    try {
      setLoading(true);
      const data = await costService.getWorkshopCostItems(workshopId);
      setWorkshopCosts(data);
    } catch (error) {
      console.error("❌ Failed to load workshop costs:", error);
      setAlertModal({
        isOpen: true,
        message: "Atölye maliyetleri yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableCostItems = async () => {
    try {
      const [itemsData, categoriesData] = await Promise.all([
        costService.getCostItems(),
        costService.getCostCategories(),
      ]);
      setAvailableCostItems(itemsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error("❌ Failed to load cost items:", error);
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({ costItemId: "", notes: "" });
    setSearchTerm("");
    setSelectedCategory("");
    setIsAddModalOpen(true);
  };

  const openEditModal = (item: WorkshopCostItem) => {
    setEditingItem(item);
    setFormData({ costItemId: item.costItemId, notes: item.notes || "" });
    setSearchTerm("");
    setSelectedCategory("");
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setEditingItem(null);
    setFormData({ costItemId: "", notes: "" });
    setSearchTerm("");
    setSelectedCategory("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workshopId) return;

    if (!formData.costItemId || formData.costItemId.trim() === "") {
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemi seçimi zorunludur!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      const itemData = {
        workshopId,
        costItemId: formData.costItemId,
        isPreferred: false,
        notes: formData.notes.trim() || undefined,
        isActive: true,
      };

      if (editingItem) {
        await costService.updateWorkshopItem(
          editingItem.workshopCostItemId,
          itemData,
        );
        setAlertModal({
          isOpen: true,
          message: "Maliyet ilişkisi başarıyla güncellendi!",
          type: "success",
        });
      } else {
        await costService.createWorkshopItem(itemData);
        setAlertModal({
          isOpen: true,
          message: "Maliyet kalemi başarıyla eklendi!",
          type: "success",
        });
      }

      closeAddModal();
      loadWorkshopCosts();
    } catch (error: any) {
      console.error("❌ Failed to save workshop cost:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "İşlem başarısız oldu!";
      setAlertModal({ isOpen: true, message: errorMessage, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (item: WorkshopCostItem) => {
    setConfirmModal({
      isOpen: true,
      message: `"${item.costItem?.itemName || item.costItemName}" maliyet kalemini bu atölyeden kaldırmak istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await costService.deleteWorkshopItem(item.workshopCostItemId);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kalemi başarıyla kaldırıldı!",
            type: "success",
          });
          loadWorkshopCosts();
        } catch (error: any) {
          console.error("❌ Failed to delete workshop cost:", error);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kalemi kaldırılamadı!",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const wcColDefs = useMemo(
    () => [
      { key: "item", value: (i: any) => i.costItemName || "" },
      { key: "category", value: (i: any) => i.categoryName || "" },
      { key: "unit", value: (i: any) => i.unitName || i.unitCode || "" },
      { key: "standard", type: "decimal", value: (i: any) => i.standardPrice ?? null },
      { key: "wprice", type: "decimal", value: (i: any) => i.workshopSpecificPrice ?? null },
      { key: "effective", type: "decimal", value: (i: any) => i.effectivePrice ?? null },
      { key: "preferred", value: (i: any) => (i.isPreferred ? "Tercih" : "Hayır") },
      { key: "notes", value: (i: any) => i.notes || "" },
    ],
    [],
  );
  const { ctx: wcCf, filtered: displayWorkshopCosts } = useTableColumnFilters(
    workshopCosts,
    wcColDefs,
  );

  // Esc: önce alt-modal, sonra ana pencere; üstte uyarı/onay varken onlar kapanır
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || alertModal.isOpen || confirmModal.isOpen) return;
      e.stopPropagation();
      if (isAddModalOpen) { if (!loading) closeAddModal(); }
      else onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAddModalOpen, alertModal.isOpen, confirmModal.isOpen, loading, onClose]);

  if (!isOpen) return null;

  const matches = (item: CostItem) => {
    if (selectedCategory && item.costCategoryId !== selectedCategory) return false;
    if (searchTerm) {
      return (
        turkishIncludes(item.itemName, searchTerm) ||
        turkishIncludes(item.costCategory?.categoryName || "", searchTerm) ||
        turkishIncludes(item.description || "", searchTerm)
      );
    }
    return true;
  };
  const listItems = availableCostItems.filter(
    (item) => formData.costItemId === item.costItemId || matches(item),
  );
  const countItems = availableCostItems.filter(matches).length;


  return (
    <>
      <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="po-modal xl wcm-modal" role="dialog" aria-modal="true" aria-labelledby="wcm-title">
          <div className="po-modal-head">
            <div>
              <span className="po-kicker">Atölye maliyetleri · {workshopCosts.length} kalem</span>
              <h3 id="wcm-title">{workshopName || "Atölye"}</h3>
            </div>
            <div className="po-modal-head-side">
              <button type="button" className="po-btn primary small" onClick={openAddModal}>{WI.plus}<span className="po-hide-sm">Maliyet kalemi ekle</span><span className="wcm-inline">Ekle</span></button>
              <button type="button" className="po-ib big" onClick={onClose} aria-label="Kapat">{WI.close}</button>
            </div>
          </div>

          <div className="po-modal-body">
            <p className="po-form-hint">Bu atölyede kullanılan maliyet kalemleri. Atölyeye özel fiyat yoksa standart fiyat geçerlidir (efektif fiyat).</p>
            {loading && workshopCosts.length === 0 ? (
              <div className="po-loading"><span className="po-spin" /> Maliyet kalemleri yükleniyor</div>
            ) : workshopCosts.length === 0 ? (
              <div className="po-blank">
                <span className="po-blank-icon">{WI.coin}</span>
                <b>Henüz maliyet kalemi eklenmemiş</b>
                Bu atölyeye maliyet kalemi ekleyerek başlayın.
                <button type="button" className="po-btn primary" onClick={openAddModal}>{WI.plus} Maliyet kalemi ekle</button>
              </div>
            ) : (
              <div className="po-table-wrap tight">
                <table className="po-table small wcm-table">
                  <thead>
                    <tr>
                      <FilterTh label="Maliyet kalemi" colKey="item" ctx={wcCf} />
                      <FilterTh label="Kategori" colKey="category" ctx={wcCf} className="po-hide-sm" />
                      <FilterTh label="Birim" colKey="unit" ctx={wcCf} className="po-hide-md" />
                      <FilterTh label="Standart" colKey="standard" type="decimal" ctx={wcCf} align="left" className="po-hide-md" />
                      <FilterTh label="Atölye fiyatı" colKey="wprice" type="decimal" ctx={wcCf} align="left" className="po-hide-sm" />
                      <FilterTh label="Efektif" colKey="effective" type="decimal" ctx={wcCf} align="left" />
                      <FilterTh label="Tercih" colKey="preferred" ctx={wcCf} className="po-hide-lg" />
                      <FilterTh label="Not" colKey="notes" ctx={wcCf} className="po-hide-lg" />
                      <th className="act"><span className="wcm-sr">İşlemler</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayWorkshopCosts.map((item) => (
                      <tr key={item.workshopCostItemId} className="static">
                        <td>
                          <div className="po-name">{item.costItemName}</div>
                          {item.costItemDescription && <div className="po-sub">{item.costItemDescription}</div>}
                          {item.categoryName && <div className="po-sub po-only-sm">{item.categoryName}</div>}
                        </td>
                        <td className="po-hide-sm">{item.categoryName ? <span className="po-badge muted">{item.categoryName}</span> : <span className="po-sub">—</span>}</td>
                        <td className="po-hide-md">
                          {item.unitName ? <>{item.unitName} <span className="po-sub">({item.unitCode})</span></> : <span className="po-sub">{item.unitCode || "—"}</span>}
                        </td>
                        <td className="po-hide-md num">
                          {item.standardPrice !== undefined ? formatCurrency(item.standardPrice, item.standardCurrency) : <span className="po-sub">—</span>}
                        </td>
                        <td className="po-hide-sm num">
                          {item.workshopSpecificPrice ? <b className="wcm-wprice">{formatCurrency(item.workshopSpecificPrice, item.workshopCurrency)}</b> : <span className="po-sub">—</span>}
                        </td>
                        <td className="num">
                          <span className="po-amt">{item.effectivePrice !== undefined ? formatCurrency(item.effectivePrice, item.effectiveCurrency) : "—"}</span>
                        </td>
                        <td className="po-hide-lg">{item.isPreferred ? <span className="po-badge ok">Tercih</span> : <span className="po-sub">—</span>}</td>
                        <td className="po-hide-lg">
                          {item.notes ? <span className="po-sub wcm-notes" title={item.notes}>{item.notes.substring(0, 30)}{item.notes.length > 30 ? "…" : ""}</span> : <span className="po-sub">—</span>}
                        </td>
                        <td className="act">
                          <div className="po-rowacts">
                            <button type="button" className="po-ib" onClick={() => openEditModal(item)} title="Düzenle" aria-label={`${item.costItemName || "Kalem"} düzenle`}>{WI.edit}</button>
                            <button type="button" className="po-ib danger" onClick={() => handleDelete(item)} title="Kaldır" aria-label={`${item.costItemName || "Kalem"} kaldır`}>{WI.trash}</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ekle / Düzenle alt-modalı */}
      {isAddModalOpen && (
        <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) closeAddModal(); }}>
          <form className="po-modal" role="dialog" aria-modal="true" aria-labelledby="wcm-add-title" onSubmit={handleSubmit}>
            <div className="po-modal-head">
              <div><span className="po-kicker">{workshopName || "Atölye"}</span><h3 id="wcm-add-title">{editingItem ? "Maliyet kalemini düzenle" : "Maliyet kalemi ekle"}</h3></div>
              <button type="button" className="po-ib big" onClick={closeAddModal} aria-label="Kapat" disabled={loading}>{WI.close}</button>
            </div>

            <div className="po-modal-body">
              <div className="po-field">
                <span>Maliyet kalemi<em>{editingItem ? "değiştirilemez" : "zorunlu"}</em></span>
                <div className="wcm-ss">
                  <div className="wcm-ss-filters">
                    <div className="po-search wcm-ss-search">
                      {WI.search}
                      <input
                        type="text"
                        aria-label="Kalem ara"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Kalem ara"
                        disabled={!!editingItem}
                      />
                    </div>
                    <select
                      className="po-select wcm-ss-cat"
                      aria-label="Kategori"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      disabled={!!editingItem}
                    >
                      <option value="">Tüm kategoriler</option>
                      {categories.map((cat) => (
                        <option key={cat.costCategoryId} value={cat.costCategoryId}>
                          {cat.categoryName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <select
                    className="wcm-ss-list"
                    aria-label="Maliyet kalemi"
                    value={formData.costItemId}
                    onChange={(e) => setFormData({ ...formData, costItemId: e.target.value })}
                    required
                    disabled={!!editingItem}
                    size={6}
                  >
                    <option value="" disabled>
                      Maliyet kalemi seçin
                    </option>
                    {listItems.map((item) => (
                      <option key={item.costItemId} value={item.costItemId}>
                        {item.itemName} - {item.costCategory?.categoryName} (
                        {formatCurrency(item.unitPrice, item.currency)}/{item.unit})
                      </option>
                    ))}
                  </select>
                  <span className="po-fieldnote">{countItems} kalem listeleniyor</span>
                </div>
              </div>

              <label className="po-field">
                <span>Not</span>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek bilgiler veya notlar"
                  rows={3}
                />
              </label>
            </div>

            <div className="po-modal-foot">
              <div className="po-foot-btns">
                <button type="button" className="po-btn" onClick={closeAddModal} disabled={loading}>Vazgeç</button>
                <button type="submit" className="po-btn primary" disabled={loading}>{loading ? "Kaydediliyor" : "Kaydet"}</button>
              </div>
            </div>
          </form>
        </div>
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Kalemi kaldır"
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Kaldır"
        cancelText="Vazgeç"
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </>
  );
};

export default WorkshopCostsModal;
